import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook to fetch holidays for calendar components.
 * Returns holiday dates for the given year and utility functions.
 */
export function useHolidays(year) {
    const [holidays, setHolidays] = useState([]);
    const [holidayDates, setHolidayDates] = useState([]);

    useEffect(() => {
        if (!year) return;
        let cancelled = false;

        const fetchHolidays = async () => {
            try {
                const data = await api.getHolidays(year);
                if (cancelled) return;
                const items = data.items || [];
                setHolidays(items);
                setHolidayDates(items.map(h => {
                    // API may return full ISO string "2026-04-23T00:00:00Z" or just date "2026-04-23"
                    const dateStr = typeof h.date === 'string' ? h.date.split('T')[0] : h.date;
                    return new Date(dateStr + 'T00:00:00');
                }));
            } catch (error) {
                if (!cancelled) console.error('Error fetching holidays:', error);
            }
        };
        fetchHolidays();

        return () => { cancelled = true; };
    }, [year]);

    const isHoliday = useCallback((date) => {
        if (!date) return false;
        const d = date.getDate();
        const m = date.getMonth();
        const y = date.getFullYear();
        return holidayDates.some(h => h.getDate() === d && h.getMonth() === m && h.getFullYear() === y);
    }, [holidayDates]);

    const getHolidayName = useCallback((date) => {
        if (!date) return null;
        const d = date.getDate();
        const m = date.getMonth();
        const y = date.getFullYear();
        const match = holidays.find(h => {
            const ds = typeof h.date === 'string' ? h.date.split('T')[0] : h.date;
            const hd = new Date(ds + 'T00:00:00');
            return hd.getDate() === d && hd.getMonth() === m && hd.getFullYear() === y;
        });
        return match?.name || null;
    }, [holidays]);

    return { holidays, holidayDates, isHoliday, getHolidayName };
}

/**
 * Hook for calendar components that tracks month changes.
 * Automatically fetches holidays for the displayed month's year.
 */
export function useCalendarHolidays(initialMonth = new Date()) {
    const [displayYear, setDisplayYear] = useState(initialMonth.getFullYear());
    const { holidays, holidayDates, isHoliday, getHolidayName } = useHolidays(displayYear);

    const onMonthChange = useCallback((month) => {
        const newYear = month.getFullYear();
        if (newYear !== displayYear) {
            setDisplayYear(newYear);
        }
    }, [displayYear]);

    return { holidays, holidayDates, isHoliday, getHolidayName, onMonthChange };
}
